import React, { useEffect, useState } from "react";
import {
  Calendar2Event,
  Clock,
  PersonCheck,
  Lock,
  InfoCircle,
  Person,
} from "react-bootstrap-icons";
import { Modal, Spinner, Container } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import "../../../assets/styles/StudentBookingModern.css";
import { sportTypeEnum } from "../../../App";
import {
  createBookingAPI,
  getTodayBookingAPI,
  getBookingDetailsAPI,
} from "../../../services/bookingServices";
import PopupMessage from "../PopupMessage";
import { useQuery } from "@tanstack/react-query";
import { useSound } from "../../../hooks/useSound";

const TIME_SLOTS = [
  "4:00 - 5:00 PM",
  "5:00 - 6:00 PM",
  "6:00 - 7:00 PM",
  "7:00 - 8:00 PM",
  "8:00 - 9:00 PM",
  "9:00 - 10:00 PM",
];

export default function BasketballSchedule() {
  const navigate = useNavigate();

  // ==========================================
  // 🔴 LOGIC SECTION (UNTOUCHED & SECURED) 🔴
  // ==========================================
  const [selectedSlot, setSelectedSlot] = useState("");
  const [teamName, setTeamName] = useState("");
  const [members, setMembers] = useState(Array(10).fill(""));
  const [feedback, setFeedback] = useState(null);
  const [todayLabel, setTodayLabel] = useState("");
  const [popup, setPopup] = useState({ show: false, type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [universityID, setUniversityID] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const playBookingSound = useSound("/sounds/success.mp3");
  const playBookingSoundError = useSound("/sounds/error.mp3");

  // 🔥 تحديد نوع الرياضة (سلة)
  const SPORT_TYPE = sportTypeEnum.basketball;

  const { data: bookings = [], refetch } = useQuery({
    queryKey: ["basketballBookings", SPORT_TYPE],
    queryFn: async () => {
      const data = await getTodayBookingAPI(SPORT_TYPE);
      if (data?.universityID) {
        setUniversityID(data.universityID);
      }
      return data?.bookings || [];
    },
    refetchInterval: 20000,
    refetchOnWindowFocus: true,
    staleTime: 5000,
  });

  useEffect(() => {
    const savedTeamName = localStorage.getItem("teamName");
    const savedMembers = localStorage.getItem("members");

    if (savedTeamName) setTeamName(savedTeamName);

    if (savedMembers) {
      const parsedMembers = JSON.parse(savedMembers);
      setMembers(parsedMembers);
    }
    formatTodayLabel();
  }, []);

  useEffect(() => {
    if (universityID) {
      setMembers((prev) => {
        const newMembers = [...prev];
        if (newMembers[0] !== universityID) {
          newMembers[0] = universityID;
          return newMembers;
        }
        return prev;
      });
    }
  }, [universityID]);

  const getConflictingMember = () => {
    const allBookedMembersToday = bookings.flatMap((b) =>
      b.members.map((id) => id.toString()),
    );
    const conflict = members.find(
      (memberId) =>
        memberId &&
        memberId.trim() !== "" &&
        allBookedMembersToday.includes(memberId.toString().trim()),
    );
    return conflict;
  };

  const conflictingMemberID = getConflictingMember();

  const formatTodayLabel = () => {
    const now = new Date();
    const day = now.toLocaleDateString("en-US", { weekday: "long" });
    const formatted = now.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    setTodayLabel(`${day}, ${formatted}`);
  };

  const slotStatus = (slot) => {
    const booking = bookings.find((b) => b.slot === slot);
    if (!booking) return "available";
    if (
      universityID &&
      booking.members.some((id) => id.toString() === universityID.toString())
    )
      return "mine";
    return booking.status || "available";
  };

  const handleSlotClick = async (slot) => {
    const status = slotStatus(slot);
    if (status === "available") {
      setSelectedSlot(slot);
      return;
    }
    if (status === "mine" || status === "confirmed") {
      try {
        setLoadingDetails(true);
        setShowModal(true);
        setModalData(null);
        const data = await getBookingDetailsAPI({
          slot,
          sportType: SPORT_TYPE,
        });
        setModalData(data);
      } catch (error) {
        setShowModal(false);
        setPopup({
          show: true,
          type: "error",
          message: error.response?.data?.message || "Access Denied",
        });
      } finally {
        setLoadingDetails(false);
      }
    }
  };

  const validateBookingForm = () => {
    if (conflictingMemberID)
      return `Student ID ${conflictingMemberID} already has a booking today!`;
    if (!teamName.trim()) return "Team name is required.";
    const trimmed = members.map((m) => m.toString().trim());
    if (trimmed.some((m) => m === ""))
      return "All 10 student IDs are required.";
    const duplicates = trimmed.filter((id, i) => trimmed.indexOf(id) !== i);
    if (duplicates.length > 0) return `Duplicate ID detected: ${duplicates[0]}`;
    for (let id of trimmed) {
      if (!/^[0-9]{9}$/.test(id)) return `Invalid ID format: ${id}`;
      if (/^(\d)\1{8}$/.test(id))
        return `Invalid ID: ${id} (all digits are the same)`;
    }
    if (universityID && trimmed[0] !== universityID.toString()) {
      return "Authentication Error: The first ID must match your University ID.";
    }
    if (!selectedSlot) return "Please select a time slot.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback(null);
    const errorMsg = validateBookingForm();
    if (errorMsg) {
      setFeedback({ type: "error", text: errorMsg });
      setPopup({ show: true, type: "error", message: errorMsg });
      return;
    }
    try {
      setIsSubmitting(true);
      localStorage.setItem("teamName", teamName);
      localStorage.setItem("members", JSON.stringify(members));

      const res = await createBookingAPI({
        sportType: SPORT_TYPE,
        slot: selectedSlot,
        teamName,
        members: members.map((m) => m.toString().trim()),
      });
      playBookingSound();
      setFeedback({ type: "info", text: res.message });
      setPopup({ show: true, type: "success", message: res.message });

      refetch();
      setSelectedSlot("");
    } catch (err) {
      playBookingSoundError();
      setFeedback({ type: "error", text: err.message });
      setPopup({ show: true, type: "error", message: err.message });
    } finally {
      setIsSubmitting(false);
    }
  };
  // ==========================================
  // 🟢 END OF LOGIC SECTION 🟢
  // ==========================================

  return (
    <div
      style={{
        paddingTop: "80px",
        paddingBottom: "80px",
        minHeight: "100vh",
        backgroundColor: "#0f172a",
        marginTop: "20px",
      }}
    >
      <Container className="d-flex flex-column align-items-center">
        {/* Main UI Wrapper */}
        <div className="w-100" style={{ maxWidth: "800px" }}>
          {/* Header Section */}
          <div className="d-flex align-items-center mb-4 gap-3 w-100">
            <button
              onClick={() => navigate(-1)}
              className="btn btn-dark rounded-circle p-0 shadow-sm border border-secondary border-opacity-25 d-flex align-items-center justify-content-center flex-shrink-0"
              style={{
                width: "45px",
                height: "45px",
                background: "rgba(255,255,255,0.05)",
              }}
            >
              <ArrowLeft size={22} className="text-white" />
            </button>
            <div className="d-flex flex-column">
              <h2
                className="m-0 fw-bold text-white"
                dir="rtl"
                style={{ fontSize: "1.6rem" }}
              >
                حجز صالة السلة
              </h2>
              <span
                className="text-info fw-semibold"
                dir="ltr"
                style={{ fontSize: "0.9rem", letterSpacing: "0.5px" }}
              >
                Basketball Booking
              </span>
            </div>
          </div>

          {/* Top Bar (Date & Info) */}
          <div
            className="mb-4 p-4 rounded-4 border border-white border-opacity-10 d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3 shadow-sm"
            style={{
              background:
                "linear-gradient(145deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)",
              backdropFilter: "blur(10px)",
            }}
          >
            <div className="d-flex align-items-center">
              <div className="bg-primary bg-opacity-25 p-3 rounded-circle me-3 flex-shrink-0 border border-primary border-opacity-25">
                <Calendar2Event size={26} className="text-info" />
              </div>
              <div className="d-flex flex-column">
                <span
                  className="fw-bold text-white mb-1"
                  style={{ fontSize: "1.1rem" }}
                >
                  {todayLabel.split(",")[0]}
                </span>
                <span className="text-secondary" style={{ fontSize: "0.9rem" }}>
                  {todayLabel.split(",")[1]}
                </span>
              </div>
            </div>
            <div className="d-flex flex-column align-items-sm-end text-sm-end">
              <span
                className="fw-bold text-light"
                dir="rtl"
                style={{ fontSize: "1rem" }}
              >
                مواعيد اليوم
              </span>
              <span className="text-secondary small" dir="ltr">
                Today's Schedule
              </span>
            </div>
          </div>

          {feedback && (
            <div
              className={`alert ${feedback.type === "error" ? "alert-danger bg-danger text-white" : "alert-info bg-info text-dark"} border-0 rounded-4 mb-4 text-center fw-semibold shadow-sm`}
            >
              {feedback.text}
            </div>
          )}

          {/* Time Slots Section */}
          <section
            className="mb-5 p-4 rounded-4 border border-white border-opacity-10 shadow-sm"
            style={{ background: "rgba(255,255,255,0.02)" }}
          >
            <div className="d-flex align-items-center justify-content-center mb-4 gap-2">
              <Clock className="text-info" size={22} />
              <h5
                className="mb-0 text-white fw-bold"
                dir="rtl"
                style={{ fontSize: "1.2rem" }}
              >
                اختر وقت الحجز
              </h5>
              <span className="text-secondary opacity-50 d-none d-sm-block">
                |
              </span>
              <span
                className="text-secondary d-none d-sm-block"
                style={{ fontSize: "0.9rem" }}
              >
                Select a time slot
              </span>
            </div>

            <div className="slot-grid gap-3">
              {TIME_SLOTS.map((slot) => {
                const status = slotStatus(slot);
                return (
                  <div
                    key={slot}
                    role="button"
                    className={`slot-${status} ${selectedSlot === slot ? "slot-selected" : ""} position-relative p-3 rounded-4 d-flex flex-column align-items-center justify-content-center`}
                    onClick={() => handleSlotClick(slot)}
                    style={{
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      minHeight: "90px",
                    }}
                  >
                    <span
                      className="fw-bold mb-2"
                      dir="ltr"
                      style={{ fontSize: "1rem" }}
                    >
                      {slot}
                    </span>

                    {/* Status Tags */}
                    {status === "available" && (
                      <span
                        className="small opacity-75 fw-medium"
                        style={{ fontSize: "0.8rem", color: "#endregion" }}
                      >
                        متاح | Available
                      </span>
                    )}
                    {status === "confirmed" && (
                      <div
                        className="d-flex align-items-center gap-1 bg-danger bg-opacity-25 border border-danger border-opacity-50 text-danger px-3 py-1 rounded-pill"
                        style={{ fontSize: "0.75rem", fontWeight: "600" }}
                      >
                        <Lock size={12} /> محجوز
                      </div>
                    )}
                    {status === "pending" && (
                      <div
                        className="d-flex align-items-center gap-1 bg-warning bg-opacity-25 border border-warning border-opacity-50 text-warning px-3 py-1 rounded-pill"
                        style={{ fontSize: "0.75rem", fontWeight: "600" }}
                      >
                        <PersonCheck size={12} /> قيد الانتظار
                      </div>
                    )}
                    {status === "mine" && (
                      <div
                        className="d-flex align-items-center gap-1 bg-info bg-opacity-25 border border-info border-opacity-50 text-info px-3 py-1 rounded-pill"
                        style={{ fontSize: "0.75rem", fontWeight: "600" }}
                      >
                        <PersonCheck size={12} /> حجزك
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Form Section */}
          <form
            onSubmit={handleSubmit}
            className="p-4 p-md-5 rounded-4 border border-white border-opacity-10 shadow-lg"
            style={{
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)",
            }}
          >
            <div className="text-center mb-4 pb-2 border-bottom border-secondary border-opacity-25">
              <h4 className="text-white fw-bold" dir="rtl">
                بيانات الفريق
              </h4>
              <p className="text-secondary small" dir="ltr">
                Team Details
              </p>
            </div>

            {/* Team Name */}
            <div className="mb-5">
              <label className="d-flex justify-content-between align-items-center w-100 px-1 mb-2">
                <span
                  dir="ltr"
                  style={{
                    color: "#94a3b8",
                    fontSize: "0.9rem",
                    fontWeight: "600",
                  }}
                >
                  Team Name
                </span>
                <span
                  dir="rtl"
                  className="fw-bold"
                  style={{ color: "#f8fafc", fontSize: "1rem" }}
                >
                  اسم الفريق
                </span>
              </label>
              <input
                className="form-control form-control-lg text-white placeholder-light"
                placeholder="Ex: The Lakers..."
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                disabled={isSubmitting}
                style={{
                  backgroundColor: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.15)",
                  fontSize: "1.05rem",
                  padding: "15px 20px",
                }}
              />
            </div>

            {/* Members 10 IDs */}
            <div className="mb-5">
              <div className="d-flex justify-content-between align-items-center w-100 px-1 mb-3">
                <span
                  dir="ltr"
                  style={{
                    color: "#94a3b8",
                    fontSize: "0.9rem",
                    fontWeight: "600",
                  }}
                >
                  Members (10 IDs)
                </span>
                <span
                  dir="rtl"
                  className="fw-bold"
                  style={{ color: "#f8fafc", fontSize: "1rem" }}
                >
                  أعضاء الفريق (10 أرقام)
                </span>
              </div>

              <div className="row g-3">
                {members.map((m, idx) => {
                  const isFirstField = idx === 0;
                  return (
                    <div key={idx} className="col-12 col-sm-6 col-md-6">
                      {" "}
                      {/* Responsive Layout */}
                      <div className="position-relative d-flex align-items-center">
                        {/* Number indicator */}
                        <div
                          className="position-absolute d-flex justify-content-center align-items-center text-secondary fw-bold"
                          style={{
                            width: "35px",
                            height: "100%",
                            borderRight: "1px solid rgba(255,255,255,0.1)",
                            fontSize: "0.85rem",
                            zIndex: 5,
                          }}
                        >
                          {idx + 1}
                        </div>

                        <input
                          className={`form-control w-100 ${isFirstField ? "fw-bold" : ""}`}
                          value={
                            isFirstField && universityID ? universityID : m
                          }
                          placeholder={isFirstField ? "Auto" : "Enter ID..."}
                          dir="ltr"
                          onChange={(e) => {
                            if (isFirstField) return;
                            setMembers((prev) => {
                              const arr = [...prev];
                              arr[idx] = e.target.value;
                              return arr;
                            });
                          }}
                          disabled={isSubmitting || isFirstField}
                          style={{
                            paddingLeft: "45px",
                            paddingRight: isFirstField ? "40px" : "15px",
                            height: "50px",
                            fontSize: "0.95rem",
                            color: isFirstField ? "#0ea5e9" : "#ffffff",
                            backgroundColor: isFirstField
                              ? "rgba(14, 165, 233, 0.08)"
                              : "rgba(255,255,255,0.04)",
                            border: isFirstField
                              ? "1px solid rgba(14, 165, 233, 0.3)"
                              : "1px solid rgba(255,255,255,0.1)",
                            transition: "border 0.3s ease",
                          }}
                        />

                        {/* Lock Icon for Leader */}
                        <div
                          className="position-absolute top-50 end-0 translate-middle-y pe-3"
                          style={{ zIndex: 5 }}
                        >
                          {isFirstField &&
                            (universityID ? (
                              <Lock size={14} className="text-info" />
                            ) : (
                              <div className="spinner-border spinner-border-sm text-info" />
                            ))}
                        </div>
                      </div>
                      {isFirstField && (
                        <div
                          className="text-info mt-1 ms-1 fw-semibold d-flex justify-content-between px-1"
                          style={{ fontSize: "0.75rem" }}
                        >
                          <span>القائد</span>
                          <span>Team Leader</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary w-100 py-3 rounded-pill d-flex flex-column align-items-center justify-content-center gap-1 border-0 shadow-lg"
              disabled={isSubmitting}
              style={{
                background: "linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)",
                transition: "transform 0.2s",
              }}
            >
              {isSubmitting ? (
                <div className="d-flex align-items-center gap-2">
                  <Spinner as="span" animation="border" size="sm" />
                  <span className="fw-bold" style={{ fontSize: "1.1rem" }}>
                    جاري التنفيذ...
                  </span>
                </div>
              ) : (
                <>
                  <span className="fw-bold" style={{ fontSize: "1.15rem" }}>
                    تأكيد الحجز {selectedSlot ? `(${selectedSlot})` : ""}
                  </span>
                  <span
                    style={{
                      fontSize: "0.85rem",
                      opacity: 0.9,
                      letterSpacing: "0.5px",
                    }}
                  >
                    Confirm Booking
                  </span>
                </>
              )}
            </button>
          </form>
        </div>
      </Container>

      {/* --- 🔥 Pro Advanced Modal (Dark Theme Applied) --- */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        dialogClassName="modal-dialog-centered"
      >
        <div
          style={{
            backgroundColor: "#1e293b",
            borderRadius: "20px",
            overflow: "hidden",
            border: "1px solid rgba(255,255,255,0.1)",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
          }}
        >
          <Modal.Header
            closeButton
            closeVariant="white"
            className="border-bottom border-white border-opacity-10 px-4 py-3 bg-dark bg-opacity-25"
          >
            <Modal.Title className="d-flex align-items-center text-white w-100">
              <div
                className="bg-primary bg-opacity-25 rounded-circle p-2 me-3 d-flex align-items-center justify-content-center flex-shrink-0"
                style={{ width: 45, height: 45 }}
              >
                <Calendar2Event size={22} className="text-info" />
              </div>
              <div className="d-flex flex-column">
                {loadingDetails ? (
                  <span style={{ fontSize: "1.1rem" }}>Loading Team...</span>
                ) : (
                  <>
                    <span
                      className="text-white fw-bold"
                      style={{ fontSize: "1.2rem" }}
                    >
                      {modalData?.teamName || "Unknown Team"}
                    </span>
                    <span
                      className="text-secondary"
                      style={{ fontSize: "0.85rem" }}
                    >
                      بيانات الفريق | Team Details
                    </span>
                  </>
                )}
              </div>
            </Modal.Title>
          </Modal.Header>

          <Modal.Body className="px-3 py-4 px-sm-4">
            {loadingDetails ? (
              <div className="text-center py-5">
                <Spinner
                  animation="border"
                  variant="info"
                  style={{ width: "2.5rem", height: "2.5rem" }}
                />
                <p className="mt-3 text-secondary fw-semibold">
                  Fetching squad data...
                </p>
              </div>
            ) : modalData ? (
              <div className="d-flex flex-column gap-3">
                {modalData.members && modalData.members.length > 0 ? (
                  modalData.members.map((member, idx) => {
                    const isCaptain = idx === 0;
                    return (
                      <div
                        key={member._id || idx}
                        className="d-flex align-items-center justify-content-between p-3 rounded-4"
                        style={{
                          background: "rgba(255,255,255,0.04)",
                          border: "1px solid rgba(255,255,255,0.05)",
                        }}
                      >
                        <div className="d-flex align-items-center">
                          {/* Avatar Section */}
                          <div className="position-relative me-3">
                            {member.profileImage ? (
                              <img
                                src={member.profileImage}
                                alt={member.fullName}
                                className="rounded-circle object-fit-cover shadow-sm"
                                style={{
                                  width: 50,
                                  height: 50,
                                  border: isCaptain
                                    ? "2px solid #0ea5e9"
                                    : "none",
                                }}
                              />
                            ) : (
                              <div
                                className="rounded-circle d-flex align-items-center justify-content-center bg-secondary bg-opacity-25 text-white shadow-sm"
                                style={{
                                  width: 50,
                                  height: 50,
                                  border: isCaptain
                                    ? "2px solid #0ea5e9"
                                    : "none",
                                }}
                              >
                                <Person size={24} />
                              </div>
                            )}
                            {isCaptain && (
                              <div
                                className="position-absolute start-50 translate-middle-x"
                                style={{ top: "-10px", color: "#f59e0b" }}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="18"
                                  height="18"
                                  fill="currentColor"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M11.668 1.564a2 2 0 1 1 3.284 2.822 2 2 0 0 1-2.924.976l-1.928 6.587A.5.5 0 0 1 9.605 12H6.395a.5.5 0 0 1-.495-.415l-1.928-6.587a2 2 0 0 1-2.924-.976 2 2 0 1 1 3.284-2.822l2.365 3.313 2.91-4.949z" />
                                </svg>
                              </div>
                            )}
                          </div>
                          {/* Info Section */}
                          <div className="d-flex flex-column">
                            <span
                              className="text-white fw-semibold"
                              style={{ fontSize: "1rem" }}
                            >
                              {member.fullName || "Unknown Player"}
                            </span>
                            <span
                              className="text-secondary text-uppercase fw-medium"
                              style={{
                                fontSize: "0.85rem",
                                letterSpacing: "1px",
                              }}
                            >
                              {member.universtyID}
                            </span>
                          </div>
                        </div>

                        {/* Admin Button */}
                        {modalData.isViewerAdmin && (
                          <Link
                            to={`/admin/view-user-profile/${member._id}`}
                            className="btn btn-sm btn-outline-info rounded-pill px-3 py-1 fw-semibold"
                            style={{ fontSize: "0.8rem" }}
                          >
                            Profile
                          </Link>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center text-secondary py-4">
                    <InfoCircle
                      size={35}
                      className="mb-3 opacity-50 text-info"
                    />
                    <p className="fw-medium">No members found.</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center text-danger py-4">
                <p className="fw-bold fs-5">Failed to load data.</p>
              </div>
            )}
          </Modal.Body>
        </div>
      </Modal>

      {popup.show && (
        <PopupMessage
          type={popup.type}
          message={popup.message}
          onClose={() => setPopup({ ...popup, show: false })}
        />
      )}
    </div>
  );
}

// import React, { useEffect, useState } from "react";
// import {
//   Calendar2Event,
//   Clock,
//   PersonCheck,
//   Lock,
//   InfoCircle,
//   Person,
// } from "react-bootstrap-icons";
// import { Modal, Spinner } from "react-bootstrap"; // Import Bootstrap Components
// import { Link } from "react-router-dom"; // Import Link for Admin Profile
// import "../../../assets/styles/StudentBookingModern.css";
// import { sportTypeEnum } from "../../../App";
// import {
//   createBookingAPI,
//   getTodayBookingAPI,
//   getBookingDetailsAPI, // ⚠️ Don't forget this import
// } from "../../../services/bookingServices";
// import PopupMessage from "../PopupMessage";
// import { useQuery } from "@tanstack/react-query";
// import { useSound } from "../../../hooks/useSound";

// const TIME_SLOTS = [
//   "4:00 - 5:00 PM",
//   "5:00 - 6:00 PM",
//   "6:00 - 7:00 PM",
//   "7:00 - 8:00 PM",
//   "8:00 - 9:00 PM",
//   "9:00 - 10:00 PM",
// ];

// export default function BasketballSchedule() {
//   // --- States ---
//   const [selectedSlot, setSelectedSlot] = useState("");
//   const [teamName, setTeamName] = useState("");
//   const [members, setMembers] = useState(Array(10).fill(""));
//   const [feedback, setFeedback] = useState(null);
//   const [todayLabel, setTodayLabel] = useState("");
//   const [popup, setPopup] = useState({ show: false, type: "", message: "" });
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [universityID, setUniversityID] = useState(null);

//   // --- Modal States (New) ---
//   const [showModal, setShowModal] = useState(false);
//   const [modalData, setModalData] = useState(null);
//   const [loadingDetails, setLoadingDetails] = useState(false);

//   const playBookingSound = useSound("/sounds/success.mp3");
//   const playBookingSoundError = useSound("/sounds/error.mp3");

//   // 🔥 تحديد نوع الرياضة
//   const SPORT_TYPE = sportTypeEnum.basketball;

//   // ------------------------------------------------
//   // 1. Fetching Data (React Query)
//   // ------------------------------------------------
//   const { data: bookings = [], refetch } = useQuery({
//     queryKey: ["basketballBookings", SPORT_TYPE],
//     queryFn: async () => {
//       const data = await getTodayBookingAPI(SPORT_TYPE);
//       if (data?.universityID) {
//         setUniversityID(data.universityID);
//       }
//       return data?.bookings || [];
//     },
//     refetchInterval: 20000,
//     refetchOnWindowFocus: true,
//     staleTime: 5000,
//   });

//   // ------------------------------------------------
//   // 2. Load LocalStorage
//   // ------------------------------------------------
//   useEffect(() => {
//     const savedTeamName = localStorage.getItem("teamName");
//     const savedMembers = localStorage.getItem("members");

//     if (savedTeamName) setTeamName(savedTeamName);

//     if (savedMembers) {
//       const parsedMembers = JSON.parse(savedMembers);
//       setMembers(parsedMembers);
//     }

//     formatTodayLabel();
//   }, []);

//   // ------------------------------------------------
//   // 3. Auto-Lock First Slot
//   // ------------------------------------------------
//   useEffect(() => {
//     if (universityID) {
//       setMembers((prev) => {
//         const newMembers = [...prev];
//         if (newMembers[0] !== universityID) {
//           newMembers[0] = universityID;
//           return newMembers;
//         }
//         return prev;
//       });
//     }
//   }, [universityID]);

//   // ------------------------------------------------
//   // 4. Conflict Check (Silent Mode)
//   // ------------------------------------------------
//   const getConflictingMember = () => {
//     const allBookedMembersToday = bookings.flatMap((b) =>
//       b.members.map((id) => id.toString()),
//     );
//     const conflict = members.find(
//       (memberId) =>
//         memberId &&
//         memberId.trim() !== "" &&
//         allBookedMembersToday.includes(memberId.toString().trim()),
//     );
//     return conflict;
//   };

//   const conflictingMemberID = getConflictingMember();

//   const formatTodayLabel = () => {
//     const now = new Date();
//     const day = now.toLocaleDateString("en-US", { weekday: "long" });
//     const formatted = now.toLocaleDateString("en-US", {
//       year: "numeric",
//       month: "long",
//       day: "numeric",
//     });
//     setTodayLabel(`${day}, ${formatted}`);
//   };

//   const slotStatus = (slot) => {
//     const booking = bookings.find((b) => b.slot === slot);
//     if (!booking) return "available";
//     if (
//       universityID &&
//       booking.members.some((id) => id.toString() === universityID.toString())
//     )
//       return "mine";
//     return booking.status || "available";
//   };

//   // --- 🔥 Handle Slot Click Logic ---
//   const handleSlotClick = async (slot) => {
//     const status = slotStatus(slot);

//     // الحالة 1: السلوت متاح -> اختاره للحجز
//     if (status === "available") {
//       setSelectedSlot(slot);
//       return;
//     }

//     // الحالة 2: السلوت محجوز -> حاول تجيب التفاصيل (الباك إند هيحمي البيانات)
//     if (status === "mine" || status === "confirmed") {
//       try {
//         setLoadingDetails(true);
//         setShowModal(true);
//         setModalData(null);

//         const data = await getBookingDetailsAPI({
//           slot,
//           sportType: SPORT_TYPE,
//         });

//         setModalData(data);
//       } catch (error) {
//         setShowModal(false);
//         setPopup({
//           show: true,
//           type: "error",
//           message:
//             error.response?.data?.message ||
//             "Access Denied / Error Fetching Data",
//         });
//       } finally {
//         setLoadingDetails(false);
//       }
//     }
//   };

//   // ------------------------------------------------
//   // VALIDATION
//   // ------------------------------------------------
//   const validateBookingForm = () => {
//     if (conflictingMemberID)
//       return `Student ID ${conflictingMemberID} already has a booking today!`;

//     if (!teamName.trim()) return "Team name is required.";

//     const trimmed = members.map((m) => m.toString().trim());
//     if (trimmed.some((m) => m === ""))
//       return "All 10 student IDs are required.";

//     const duplicates = trimmed.filter((id, i) => trimmed.indexOf(id) !== i);
//     if (duplicates.length > 0) return `Duplicate ID detected: ${duplicates[0]}`;

//     for (let id of trimmed) {
//       if (!/^[0-9]{9}$/.test(id)) return `Invalid ID format: ${id}`;
//       if (/^(\d)\1{8}$/.test(id))
//         return `Invalid ID: ${id} (all digits are the same)`;
//     }

//     if (universityID && trimmed[0] !== universityID.toString()) {
//       return "Authentication Error: The first ID must match your University ID.";
//     }

//     if (!selectedSlot) return "Please select a time slot.";

//     return null;
//   };

//   // ------------------------------------------------
//   // SUBMIT
//   // ------------------------------------------------
//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setFeedback(null);

//     const errorMsg = validateBookingForm();
//     if (errorMsg) {
//       setFeedback({ type: "error", text: errorMsg });
//       setPopup({ show: true, type: "error", message: errorMsg });
//       return;
//     }

//     try {
//       setIsSubmitting(true);
//       localStorage.setItem("teamName", teamName);
//       localStorage.setItem("members", JSON.stringify(members));

//       const res = await createBookingAPI({
//         sportType: SPORT_TYPE,
//         slot: selectedSlot,
//         teamName,
//         members: members.map((m) => m.toString().trim()),
//       });

//       playBookingSound();
//       setFeedback({ type: "info", text: res.message });
//       setPopup({ show: true, type: "success", message: res.message });

//       refetch();
//       setSelectedSlot("");
//     } catch (err) {
//       playBookingSoundError();
//       setFeedback({ type: "error", text: err.message });
//       setPopup({ show: true, type: "error", message: err.message });
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   // ------------------------------------------------
//   // RENDER
//   // ------------------------------------------------
//   return (
//     <div className="container py-4 mt-5">
//       <div className="text-center mb-4 mt-5">
//         <h4 className="text-primary">Basketball Booking</h4>
//       </div>

//       <div className="top-bar mb-4">
//         <div className="d-flex align-items-center justify-content-between flex-wrap">
//           <div className="d-flex align-items-center">
//             <Calendar2Event size={34} className="me-3 text-primary" />
//             <div>
//               <div className="today-day">{todayLabel.split(",")[0]}</div>
//               <div className="small-muted">{todayLabel.split(",")[1]}</div>
//             </div>
//           </div>
//           <div className="text-end">
//             <div className="today-bookings">Today's bookings</div>
//           </div>
//         </div>
//       </div>

//       {feedback && (
//         <div className={`mb-3 feedback-${feedback.type}`}>{feedback.text}</div>
//       )}

//       <section className="mb-4">
//         <div className="d-flex align-items-center mb-3">
//           <Clock className="me-2 text-primary" />
//           <h5 className="mb-0 text-primary">Choose a time slot</h5>
//         </div>
//         <div className="slot-grid mb-3">
//           {TIME_SLOTS.map((slot) => {
//             const status = slotStatus(slot);
//             return (
//               <div
//                 key={slot}
//                 role="button"
//                 className={`slot-${status} ${
//                   selectedSlot === slot ? "slot-selected" : ""
//                 }`}
//                 // 👇 onClick Updated
//                 onClick={() => handleSlotClick(slot)}
//               >
//                 <div>{slot}</div>
//                 {status === "confirmed" && (
//                   <div className="slot-note">
//                     <Lock size={12} /> Booked
//                   </div>
//                 )}
//                 {status === "pending" && (
//                   <div className="slot-note-pending">
//                     <PersonCheck size={12} /> Pending
//                   </div>
//                 )}
//                 {status === "mine" && (
//                   <div className="slot-note-mine">
//                     <PersonCheck size={12} /> You
//                   </div>
//                 )}
//               </div>
//             );
//           })}
//         </div>
//       </section>

//       <form onSubmit={handleSubmit}>
//         <div className="mb-3">
//           <label className="form-label fw-semibold">Team name</label>
//           <input
//             className="form-control form-control-lg"
//             placeholder="Your team name"
//             value={teamName}
//             onChange={(e) => setTeamName(e.target.value)}
//             disabled={isSubmitting}
//           />
//         </div>

//         <div className="mb-2">
//           <div className="fw-semibold mb-2">Team members (10 IDs)</div>
//           <div className="row g-2">
//             {members.map((m, idx) => {
//               const isFirstField = idx === 0;

//               return (
//                 <div key={idx} className="col-6 col-md-3">
//                   <div className="position-relative">
//                     <input
//                       className={`form-control member-input
//                         ${isFirstField ? "bg-light text-primary fw-bold" : ""}
//                       `}
//                       value={isFirstField && universityID ? universityID : m}
//                       placeholder={
//                         isFirstField ? "Your ID (Auto)" : `ID ${idx + 1}`
//                       }
//                       onChange={(e) => {
//                         if (isFirstField) return;
//                         setMembers((prev) => {
//                           const arr = [...prev];
//                           arr[idx] = e.target.value;
//                           return arr;
//                         });
//                       }}
//                       disabled={isSubmitting || isFirstField}
//                       style={
//                         isFirstField
//                           ? { borderColor: "#0d6efd", cursor: "not-allowed" }
//                           : {}
//                       }
//                     />

//                     <div className="position-absolute top-50 end-0 translate-middle-y me-2">
//                       {isFirstField &&
//                         (universityID ? (
//                           <Lock size={14} className="text-primary" />
//                         ) : (
//                           <div className="spinner-border spinner-border-sm text-primary" />
//                         ))}
//                     </div>
//                   </div>
//                   {isFirstField && (
//                     <div
//                       style={{ fontSize: "0.7rem" }}
//                       className="text-muted ms-1"
//                     >
//                       You (Team Leader)
//                     </div>
//                   )}
//                 </div>
//               );
//             })}
//           </div>
//         </div>

//         <button
//           type="submit"
//           className="btn btn-primary w-100 mt-3"
//           disabled={isSubmitting}
//         >
//           {isSubmitting
//             ? "Sending..."
//             : `Confirm Booking ${selectedSlot || ""}`}
//         </button>
//       </form>

//       {/* --- 🔥 Pro Advanced Modal (Consistent with Football) --- */}
//       <Modal
//         show={showModal}
//         onHide={() => setShowModal(false)}
//         centered
//         dialogClassName="pro-modal"
//       >
//         <Modal.Header
//           closeButton
//           closeVariant="white"
//           className="pro-modal-header"
//         >
//           <Modal.Title className="pro-modal-title d-flex align-items-center">
//             <div
//               className="bg-white bg-opacity-25 rounded-circle p-2 me-3 d-flex align-items-center justify-content-center"
//               style={{ width: 40, height: 40 }}
//             >
//               <Calendar2Event size={20} className="text-white" />
//             </div>
//             <div>
//               {loadingDetails ? (
//                 "Loading Team..."
//               ) : (
//                 <>
//                   <div
//                     style={{
//                       fontSize: "0.8rem",
//                       opacity: 0.8,
//                       fontWeight: "normal",
//                     }}
//                   >
//                     Team Details
//                   </div>
//                   {modalData?.teamName || "Unknown Team"}
//                 </>
//               )}
//             </div>
//           </Modal.Title>
//         </Modal.Header>

//         <Modal.Body className="pro-modal-body">
//           {loadingDetails ? (
//             <div className="text-center py-5">
//               <Spinner
//                 animation="border"
//                 variant="primary"
//                 style={{ width: "3rem", height: "3rem" }}
//               />
//               <p className="mt-3 text-muted fw-semibold">
//                 Fetching squad data...
//               </p>
//             </div>
//           ) : modalData ? (
//             <div className="d-flex flex-column">
//               {modalData.members && modalData.members.length > 0 ? (
//                 modalData.members.map((member, idx) => {
//                   const isCaptain = idx === 0;

//                   return (
//                     <div key={member._id || idx} className="player-card">
//                       <div className="d-flex align-items-center">
//                         {/* Avatar Section */}
//                         <div className="position-relative">
//                           {member.profileImage ? (
//                             <img
//                               src={member.profileImage}
//                               alt={member.fullName}
//                               className={`player-avatar ${
//                                 isCaptain ? "is-captain-border" : ""
//                               }`}
//                             />
//                           ) : (
//                             <div
//                               className={`player-avatar d-flex align-items-center justify-content-center bg-light text-secondary ${
//                                 isCaptain ? "is-captain-border" : ""
//                               }`}
//                             >
//                               <Person size={24} />
//                             </div>
//                           )}

//                           {/* Crown Icon for Captain */}
//                           {isCaptain && (
//                             <div
//                               className="position-absolute start-50 translate-middle-x"
//                               style={{
//                                 top: "-12px",
//                                 color: "#ffc107",
//                                 filter:
//                                   "drop-shadow(0px 2px 2px rgba(0,0,0,0.2))",
//                               }}
//                             >
//                               <svg
//                                 xmlns="http://www.w3.org/2000/svg"
//                                 width="16"
//                                 height="16"
//                                 fill="currentColor"
//                                 viewBox="0 0 16 16"
//                               >
//                                 <path d="M11.668 1.564a2 2 0 1 1 3.284 2.822 2 2 0 0 1-2.924.976l-1.928 6.587A.5.5 0 0 1 9.605 12H6.395a.5.5 0 0 1-.495-.415l-1.928-6.587a2 2 0 0 1-2.924-.976 2 2 0 1 1 3.284-2.822l2.365 3.313 2.91-4.949z" />
//                               </svg>
//                             </div>
//                           )}
//                         </div>

//                         {/* Info Section */}
//                         <div className="ms-3 player-info">
//                           <div className="d-flex align-items-center">
//                             <h6>{member.fullName || "Unknown Player"}</h6>
//                             {isCaptain && (
//                               <span className="captain-badge">Captain</span>
//                             )}
//                           </div>
//                           <span className="text-uppercase">
//                             {member.universtyID}
//                           </span>
//                         </div>
//                       </div>

//                       {/* Admin Button */}
//                       {modalData.isViewerAdmin && (
//                         <Link
//                           to={`/admin/view-user-profile/${member._id}`}
//                           className="btn-view-profile text-decoration-none"
//                         >
//                           Profile
//                         </Link>
//                       )}
//                     </div>
//                   );
//                 })
//               ) : (
//                 <div className="text-center text-muted py-4">
//                   <InfoCircle size={30} className="mb-2 opacity-50" />
//                   <p>No members found in this team.</p>
//                 </div>
//               )}
//             </div>
//           ) : (
//             <div className="text-center text-danger py-4">
//               <p className="fw-bold">Failed to load data.</p>
//             </div>
//           )}
//         </Modal.Body>
//       </Modal>

//       {popup.show && (
//         <PopupMessage
//           type={popup.type}
//           message={popup.message}
//           onClose={() => setPopup({ ...popup, show: false })}
//         />
//       )}
//     </div>
//   );
// }
